import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Globe,
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Sparkles,
  X,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { vendorService } from '../../../services/api';
import { useToast } from '../../../hooks/useToast';

const PAGE_TYPES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'STATIC', label: 'Static Page' },
  { value: 'LISTING', label: 'Listing Page' },
  { value: 'CATEGORY', label: 'Category Page' },
  { value: 'CUSTOM', label: 'Custom URL' },
];

const ROBOTS_OPTIONS = [
  { value: 'index, follow', label: 'index, follow (Recommended for Google)' },
  { value: 'noindex, follow', label: 'noindex, follow' },
  { value: 'noindex, nofollow', label: 'noindex, nofollow (Do Not Index)' },
  { value: 'index, nofollow', label: 'index, nofollow' },
];

const DEFAULT_STATIC_ROUTES = [
  { url: '/', name: 'Home Page', type: 'STATIC' },
  { url: '/about-us', name: 'About Us Page', type: 'STATIC' },
  { url: '/interior-designers-and-architects', name: 'Interior Designers & Architects', type: 'CATEGORY' },
  { url: '/listing', name: 'All Listings Directory', type: 'CATEGORY' },
  { url: '/terms-conditions', name: 'Terms & Conditions', type: 'STATIC' },
  { url: '/privacy-policy', name: 'Privacy Policy', type: 'STATIC' },
  { url: '/refund-cancellation-policy', name: 'Refund Policy', type: 'STATIC' },
  { url: '/login', name: 'Login Page', type: 'STATIC' },
  { url: '/sign-up', name: 'Sign Up Page', type: 'STATIC' },
];

const SeoManagementTab = () => {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(12);

  const [search, setSearch] = useState('');
  const [pageTypeFilter, setPageTypeFilter] = useState('ALL');

  const [quickRoutes, setQuickRoutes] = useState({
    staticRoutes: DEFAULT_STATIC_ROUTES,
    categoryRoutes: [],
    sampleListings: []
  });

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form Fields - streamlined to only essential SEO fields
  const [formData, setFormData] = useState({
    page_url: '/',
    page_name: 'Home Page',
    page_type: 'STATIC',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    robots: 'index, follow',
  });

  const [urlSelectionMode, setUrlSelectionMode] = useState('quick'); // 'quick' | 'custom'

  // Delete Confirm State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  // Live Auto-complete Suggestions State
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);
  const isSuggestionSelectedRef = useRef(false);

  // Fetch quick routes from backend
  useEffect(() => {
    vendorService.getAdminSeoQuickRoutes().then((res) => {
      const data = res?.data || res;
      if (data && data.staticRoutes && data.staticRoutes.length > 0) {
        setQuickRoutes(data);
      }
    }).catch(() => {});
  }, []);

  // Debounced Live Suggestions Fetch
  useEffect(() => {
    if (isSuggestionSelectedRef.current) {
      isSuggestionSelectedRef.current = false;
      return;
    }

    const trimmed = search.trim();
    if (!trimmed) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await vendorService.getAdminSeoSuggestions(trimmed);
        const list = res?.data || res;
        if (Array.isArray(list)) {
          setSuggestions(list);
          setShowSuggestions(list.length > 0);
        }
      } catch (_) {
        setSuggestions([]);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [search]);

  // Click Outside to Dismiss Suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting a suggestion
  const handleSelectSuggestion = (item) => {
    isSuggestionSelectedRef.current = true;
    setShowSuggestions(false);
    setSuggestions([]);
    setSearch(item.url);
    setPage(0);
  };

  // Fetch rules list
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminSeoList(page, pageSize, search, pageTypeFilter, null);
      const data = res?.data || res;
      if (res?.success !== false && data && Array.isArray(data.content)) {
        setRules(data.content);
        setTotalElements(data.totalElements || 0);
        setTotalPages(data.totalPages || 0);
      } else {
        setRules([]);
        if (res?.error) {
          toast.error(res.error);
        }
      }
    } catch (err) {
      console.error('Failed to load SEO rules', err);
      toast.error('Failed to load SEO rules');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, pageTypeFilter, toast]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Handle open modal for create
  const handleOpenCreateModal = () => {
    setEditingRule(null);
    setUrlSelectionMode('quick');
    setFormData({
      page_url: '/',
      page_name: 'Home Page',
      page_type: 'STATIC',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      robots: 'index, follow',
    });
    setModalOpen(true);
  };

  // Handle open modal for edit
  const handleOpenEditModal = (rule) => {
    setEditingRule(rule);
    setUrlSelectionMode('custom');
    setFormData({
      page_url: rule.page_url,
      page_name: rule.page_name,
      page_type: rule.page_type,
      meta_title: rule.meta_title,
      meta_description: rule.meta_description || '',
      meta_keywords: rule.meta_keywords || '',
      robots: rule.robots || 'index, follow',
    });
    setModalOpen(true);
  };

  // Quick route select handler
  const handleSelectQuickRoute = (e) => {
    const val = e.target.value;
    if (!val) return;

    if (val === 'CUSTOM_MANUAL') {
      setUrlSelectionMode('custom');
      setFormData((prev) => ({ ...prev, page_url: '', page_name: '' }));
      return;
    }

    const allRoutes = [
      ...quickRoutes.staticRoutes,
      ...(quickRoutes.categoryRoutes || []),
      ...quickRoutes.sampleListings
    ];
    const match = allRoutes.find((r) => r.url === val);
    if (match) {
      setFormData((prev) => ({
        ...prev,
        page_url: match.url,
        page_name: match.name,
        page_type: match.type || 'STATIC',
      }));
    }
  };

  // Submit create or edit
  const handleSubmit = async (e) => {
    e.preventDefault();

    let cleanUrl = (formData.page_url || '').trim();
    if (!cleanUrl) {
      toast.error('Page URL path is required');
      return;
    }

    // Auto-clean domain if admin pasted full browser URL
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      try {
        const parsed = new URL(cleanUrl);
        cleanUrl = parsed.pathname || '/';
      } catch (_) {
        cleanUrl = cleanUrl.replace(/^https?:\/\/[^/]+/i, '');
      }
    } else if (cleanUrl.includes('://')) {
      cleanUrl = '/' + cleanUrl.split('://', 2)[1].split('/', 2)[1] || '/';
    }

    if (!cleanUrl.startsWith('/')) {
      cleanUrl = '/' + cleanUrl;
    }
    if (cleanUrl.length > 1 && cleanUrl.endsWith('/')) {
      cleanUrl = cleanUrl.slice(0, -1);
    }

    if (!formData.meta_title.trim()) {
      toast.error('Meta Title is required');
      return;
    }

    const payload = {
      page_url: cleanUrl,
      page_name: (formData.page_name || '').trim(),
      page_type: formData.page_type || 'STATIC',
      meta_title: formData.meta_title.trim(),
      meta_description: (formData.meta_description || '').trim(),
      meta_keywords: (formData.meta_keywords || '').trim(),
      robots: formData.robots || 'index, follow',
    };

    setIsSubmitting(true);
    try {
      const res = editingRule
        ? await vendorService.updateAdminSeo(editingRule.id, payload)
        : await vendorService.createAdminSeo(payload);

      if (res && res.success !== false) {
        toast.success(editingRule ? `SEO rule for '${cleanUrl}' updated successfully` : `SEO rule for '${cleanUrl}' created successfully`);
        setModalOpen(false);
        fetchRules();
      } else {
        toast.error(res?.error || 'Error saving SEO configuration');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error saving SEO configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete rule
  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;
    try {
      const res = await vendorService.deleteAdminSeo(ruleToDelete.id);
      if (res && res.success !== false) {
        toast.success(`SEO rule for '${ruleToDelete.page_url}' deleted`);
        setDeleteModalOpen(false);
        setRuleToDelete(null);
        fetchRules();
      } else {
        toast.error(res?.error || 'Failed to delete SEO rule');
      }
    } catch (err) {
      toast.error('Failed to delete SEO rule');
    }
  };

  // Title character counter badge color
  const getTitleCountBadge = (count) => {
    if (count === 0) return 'text-slate-400';
    if (count >= 40 && count <= 60) return 'text-emerald-500 font-semibold';
    if (count > 60) return 'text-amber-500 font-semibold';
    return 'text-slate-500';
  };

  // Description character counter badge color
  const getDescCountBadge = (count) => {
    if (count === 0) return 'text-slate-400';
    if (count >= 120 && count <= 160) return 'text-emerald-500 font-semibold';
    if (count > 160) return 'text-amber-500 font-semibold';
    return 'text-slate-500';
  };

  return (
    <div className="space-y-6">
      {/* ── Top Header & Stats ─────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 rounded-xl bg-teal-50 text-[#2ab391]">
              <Globe className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Dynamic SEO Management</h2>
              <p className="text-xs text-slate-500">
                Manage live Meta Titles, Descriptions, Keywords & Google SERP parameters per URL without code changes
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchRules}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
            title="Refresh list"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-[#2ab391] hover:bg-[#23997c] rounded-xl shadow-sm shadow-[#2ab391]/20 transition cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Add SEO Rule
          </button>
        </div>
      </div>

      {/* ── Filters & Search Bar ───────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm">
        <div ref={searchContainerRef} className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              isSuggestionSelectedRef.current = false;
              setSearch(e.target.value);
              setPage(0);
            }}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowSuggestions(false);
              }
            }}
            placeholder="Search by URL path, company name, or title (e.g. Sky Moduler)..."
            className="w-full pl-9 pr-9 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                isSuggestionSelectedRef.current = true;
                setSearch('');
                setPage(0);
                setSuggestions([]);
                setShowSuggestions(false);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition p-0.5 rounded cursor-pointer"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Autocomplete Suggestions Popover */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-40 overflow-hidden divide-y divide-slate-100 max-h-72 overflow-y-auto">
              <div className="px-3.5 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                <span>Matching SEO Pages & Listings</span>
                <span>{suggestions.length} found</span>
              </div>
              {suggestions.map((item, idx) => (
                <div
                  key={`${item.url}-${idx}`}
                  onClick={() => handleSelectSuggestion(item)}
                  className="px-3.5 py-2.5 hover:bg-slate-50/90 cursor-pointer transition flex items-center justify-between gap-3 group"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-xs text-slate-800 truncate group-hover:text-[#2ab391] transition">
                        {item.title}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide ${
                        item.type === 'STATIC'
                          ? 'bg-blue-50 text-blue-700'
                          : item.type === 'LISTING'
                          ? 'bg-purple-50 text-purple-700'
                          : item.type === 'CATEGORY'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {item.type}
                      </span>
                      {item.is_configured ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                          <CheckCircle2 className="w-3 h-3" />
                          Configured
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">
                          Not in SEO
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-mono text-slate-500 truncate mt-0.5">
                      {item.url}
                    </p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#2ab391] group-hover:translate-x-0.5 transition shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={pageTypeFilter}
              onChange={(e) => {
                setPageTypeFilter(e.target.value);
                setPage(0);
              }}
              className="bg-transparent border-none focus:outline-none text-xs font-medium text-slate-700 cursor-pointer"
            >
              {PAGE_TYPES.map((pt) => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Rules Table ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="py-3.5 px-4">URL Route</th>
                <th className="py-3.5 px-4">Page Name</th>
                <th className="py-3.5 px-4">Type</th>
                <th className="py-3.5 px-4">Meta Title</th>
                <th className="py-3.5 px-4">Robots</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-400">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-[#2ab391]" />
                      Loading SEO rules...
                    </div>
                  </td>
                </tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-500">
                    <div className="max-w-xs mx-auto text-center space-y-2">
                      <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto text-slate-400">
                        <Globe className="w-6 h-6" />
                      </div>
                      <p className="font-semibold text-slate-700">No SEO rules found</p>
                      <p className="text-[11px] text-slate-400">
                        Try adjusting your search query or add a new SEO configuration for any project URL.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-slate-50/60 transition group">
                    <td className="py-3.5 px-4 font-mono font-medium text-slate-700">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate max-w-[200px]" title={rule.page_url}>
                          {rule.page_url}
                        </span>
                        <a
                          href={rule.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-[#2ab391] transition"
                          title="Open URL in new tab"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 font-medium text-slate-800">
                      {rule.page_name}
                    </td>

                    <td className="py-3.5 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${
                        rule.page_type === 'STATIC'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200/60'
                          : rule.page_type === 'LISTING'
                          ? 'bg-purple-50 text-purple-700 border border-purple-200/60'
                          : rule.page_type === 'CATEGORY'
                          ? 'bg-amber-50 text-amber-700 border border-amber-200/60'
                          : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}>
                        {rule.page_type}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 max-w-[260px] truncate text-slate-600" title={rule.meta_title}>
                      {rule.meta_title}
                    </td>

                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${
                        rule.robots?.includes('index') && !rule.robots?.includes('noindex')
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-rose-50 text-rose-700'
                      }`}>
                        {rule.robots || 'index, follow'}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleOpenEditModal(rule)}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-[#2ab391] hover:bg-teal-50 transition cursor-pointer"
                          title="Edit SEO"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setRuleToDelete(rule);
                            setDeleteModalOpen(true);
                          }}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition cursor-pointer"
                          title="Delete SEO"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ────────────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50/70 border-t border-slate-200 text-xs text-slate-500">
            <div>
              Showing <span className="font-semibold text-slate-700">{rules.length}</span> of{' '}
              <span className="font-semibold text-slate-700">{totalElements}</span> rules
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-2.5 py-1 rounded-md border border-slate-200 bg-white font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
              >
                Prev
              </button>
              <span className="px-2 font-medium">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-2.5 py-1 rounded-md border border-slate-200 bg-white font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Add / Edit SEO Modal ────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl border border-slate-200 overflow-hidden my-8">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#2ab391]" />
                <h3 className="font-bold text-slate-800 text-base">
                  {editingRule ? `Edit SEO Rule: ${editingRule.page_url}` : 'Create New SEO Rule'}
                </h3>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* URL & Page Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Target URL Path <span className="text-rose-500">*</span>
                  </label>
                  {!editingRule && urlSelectionMode === 'quick' ? (
                    <div className="space-y-1.5">
                      <select
                        onChange={handleSelectQuickRoute}
                        value={formData.page_url}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
                      >
                        <optgroup label="Standard System Pages">
                          {quickRoutes.staticRoutes.map((r) => (
                            <option key={r.url} value={r.url}>
                              {r.name} ({r.url})
                            </option>
                          ))}
                        </optgroup>
                        {quickRoutes.categoryRoutes && quickRoutes.categoryRoutes.length > 0 && (
                          <optgroup label="Category & Filter Directories">
                            {quickRoutes.categoryRoutes.map((r) => (
                              <option key={r.url} value={r.url}>
                                {r.name} ({r.url})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {quickRoutes.sampleListings.length > 0 && (
                          <optgroup label="Recent Active Listings (Clean Slugs)">
                            {quickRoutes.sampleListings.map((r) => (
                              <option key={r.url} value={r.url}>
                                {r.name} ({r.url})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <option value="CUSTOM_MANUAL">✏️ Enter Custom URL...</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => setUrlSelectionMode('custom')}
                        className="text-[11px] text-[#2ab391] hover:underline font-semibold"
                      >
                        Switch to Custom URL input
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <input
                        type="text"
                        required
                        value={formData.page_url}
                        onChange={(e) => setFormData({ ...formData, page_url: e.target.value })}
                        placeholder="/about-us or /listing/42"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
                      />
                      {!editingRule && (
                        <button
                          type="button"
                          onClick={() => setUrlSelectionMode('quick')}
                          className="text-[11px] text-[#2ab391] hover:underline font-semibold"
                        >
                          Switch to Quick Select dropdown
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Page Name / Label <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_name}
                    onChange={(e) => setFormData({ ...formData, page_name: e.target.value })}
                    placeholder="e.g. About Us Page"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
                  />
                </div>
              </div>

              {/* Page Type & Robots */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Page Classification
                  </label>
                  <select
                    value={formData.page_type}
                    onChange={(e) => setFormData({ ...formData, page_type: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30"
                  >
                    <option value="STATIC">STATIC (Website Static Page)</option>
                    <option value="LISTING">LISTING (Specific Listing Profile)</option>
                    <option value="CATEGORY">CATEGORY (Category Directory)</option>
                    <option value="CUSTOM">CUSTOM (Landing Page / Filter URL)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Robots Directives (Search Indexing)
                  </label>
                  <select
                    value={formData.robots}
                    onChange={(e) => setFormData({ ...formData, robots: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30"
                  >
                    {ROBOTS_OPTIONS.map((ro) => (
                      <option key={ro.value} value={ro.value}>{ro.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Meta Title with Live Character Counter */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700">
                    Meta Title <span className="text-rose-500">*</span>
                  </label>
                  <span className={`text-[11px] ${getTitleCountBadge(formData.meta_title.length)}`}>
                    {formData.meta_title.length} / 60 characters recommended
                  </span>
                </div>
                <input
                  type="text"
                  required
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  placeholder="e.g. Best Interior Designers & Architects in Mumbai | Top Interiors India"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
                />
              </div>

              {/* Meta Description with Live Character Counter */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700">
                    Meta Description
                  </label>
                  <span className={`text-[11px] ${getDescCountBadge(formData.meta_description.length)}`}>
                    {formData.meta_description.length} / 160 characters recommended
                  </span>
                </div>
                <textarea
                  rows="3"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="Enter a compelling 150-160 character summary that will encourage users to click in Google search results..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
                />
              </div>

              {/* ── Live Interactive Google SERP Preview ───────────────────── */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    Live Google Search Result Preview (SERP)
                  </span>
                  <span className="text-[10px] text-slate-400">Desktop / Mobile Snippet</span>
                </div>

                <div className="p-4 bg-white rounded-lg border border-slate-200/80 shadow-xs space-y-1">
                  {/* Google Favicon & URL */}
                  <div className="flex items-center gap-2 text-[12px] text-slate-600 truncate">
                    <div className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center shrink-0 border border-slate-200">
                      <span className="text-[9px] font-bold text-[#2ab391]">T</span>
                    </div>
                    <span className="truncate">
                      vendor.topinteriorsindia.com {formData.page_url ? `› ${formData.page_url.replace(/^\//, '')}` : ''}
                    </span>
                  </div>

                  {/* Google Blue Title */}
                  <h4 className="text-[16px] text-[#1a0dab] hover:underline font-normal cursor-pointer line-clamp-1 leading-snug">
                    {formData.meta_title || 'Example Page Title - Top Interiors India'}
                  </h4>

                  {/* Google Snippet */}
                  <p className="text-[13px] text-[#4d5156] line-clamp-2 leading-relaxed">
                    {formData.meta_description ||
                      'Find verified interior designers, architects, furniture suppliers, and home decor vendors in India on Top Interiors India.'}
                  </p>
                </div>
              </div>

              {/* Meta Keywords */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Meta Keywords (Comma separated)
                </label>
                <input
                  type="text"
                  value={formData.meta_keywords}
                  onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                  placeholder="e.g. interior design, architects, furniture"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391]"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 text-xs font-bold text-white bg-[#2ab391] hover:bg-[#23997c] rounded-xl shadow-sm transition disabled:opacity-50 cursor-pointer"
                >
                  {isSubmitting ? 'Saving...' : editingRule ? 'Update SEO Rule' : 'Create SEO Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {deleteModalOpen && ruleToDelete && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="p-2.5 rounded-full bg-rose-50">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-800 text-base">Delete SEO Rule?</h3>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to delete the custom SEO rule for{' '}
              <strong className="text-slate-800 font-mono">{ruleToDelete.page_url}</strong>? Once deleted, the system
              will automatically revert to automated dynamic fallback SEO for this page.
            </p>
            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl transition shadow-sm"
              >
                Delete Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoManagementTab;
